'use client';

import dynamic from 'next/dynamic';
import type { FC, ReactNode } from 'react';
import { Component, useEffect, useRef, useState } from 'react';
import { useStore } from '@/app/providers/store';
import { usePathname } from '@/i18n/routing';

const ActionDialog = dynamic(() => import('@/app/components/shared/ActionDialog').then((mod) => mod.ActionDialog), {
  loading: () => null,
  ssr: false,
});
const AgentInviteDialog = dynamic(
  () => import('@/app/components/shared/AgentInviteDialog').then((mod) => mod.AgentInviteDialog),
  { loading: () => null, ssr: false },
);
const ConfirmDialog = dynamic(() => import('@/app/components/shared/ConfirmDialog').then((mod) => mod.ConfirmDialog), {
  loading: () => null,
  ssr: false,
});
const AdmissionConfirmDialog = dynamic(
  () => import('@/app/components/shared/AdmissionConfirmDialog').then((mod) => mod.AdmissionConfirmDialog),
  { loading: () => null, ssr: false },
);
const CouponDialog = dynamic(() => import('@/app/components/shared/CouponDialog').then((mod) => mod.CouponDialog), {
  loading: () => null,
  ssr: false,
});
const SimpleCreationDetailModal = dynamic(
  () =>
    import('@/app/components/shared/CreationDetail/SimpleCreationDetailModal').then(
      (mod) => mod.SimpleCreationDetailModal,
    ),
  { loading: () => null, ssr: false },
);
const CreditDetailsDialog = dynamic(
  () => import('@/app/components/shared/CreditDetails').then((mod) => mod.CreditDetailsDialog),
  { loading: () => null, ssr: false },
);
const DeleteDialog = dynamic(() => import('@/app/components/shared/DeleteDialog').then((mod) => mod.DeleteDialog), {
  loading: () => null,
  ssr: false,
});
const FlyOverlay = dynamic(() => import('@/app/components/shared/FlyOverlay').then((mod) => mod.FlyOverlay), {
  loading: () => null,
  ssr: false,
});
const FrameShotDialog = dynamic(
  () => import('@/app/components/shared/FrameShotDialog').then((mod) => mod.FrameShotDialog),
  { loading: () => null, ssr: false },
);
const TaskCenterDrawer = dynamic(
  () => import('@/app/components/shared/TaskCenterDrawer').then((mod) => mod.TaskCenterDrawer),
  { loading: () => null, ssr: false },
);
const GoogleRewardedAd = dynamic(
  () => import('@/app/components/shared/GoogleAds').then((mod) => mod.GoogleRewardedAd),
  { loading: () => null, ssr: false },
);
const InsufficientCreditsAdsDialog = dynamic(
  () => import('@/app/components/shared/InsufficientCreditsAdsDialog').then((mod) => mod.InsufficientCreditsAdsDialog),
  { loading: () => null, ssr: false },
);
const LoginDialog = dynamic(() => import('@/app/components/shared/LoginDialog').then((mod) => mod.LoginDialog), {
  loading: () => null,
  ssr: false,
});
const NewsletterSubscriptionDialog = dynamic(
  () => import('@/app/components/shared/NewsletterSubscriptionDialog').then((mod) => mod.NewsletterSubscriptionDialog),
  { loading: () => null, ssr: false },
);
const NoWatermarkAdsDialog = dynamic(
  () => import('@/app/components/shared/NoWatermarkAdsDialog').then((mod) => mod.NoWatermarkAdsDialog),
  { loading: () => null, ssr: false },
);
const PlanDetailDialog = dynamic(
  () => import('@/app/components/shared/PlanDetailDialog').then((mod) => mod.PlanDetailDialog),
  { loading: () => null, ssr: false },
);
const PostsModal = dynamic(() => import('@/app/components/shared/PostsModal').then((mod) => mod.PostsModal), {
  loading: () => null,
  ssr: false,
});
const PurchaseCreditsDialog = dynamic(
  () => import('@/app/components/shared/PurchaseCreditsDialog').then((mod) => mod.PurchaseCreditsDialog),
  { loading: () => null, ssr: false },
);
const Questionnaire = dynamic(() => import('@/app/components/shared/Questionnaire').then((mod) => mod.Questionnaire), {
  loading: () => null,
  ssr: false,
});
const ShareDialog = dynamic(() => import('@/app/components/shared/ShareDialog').then((mod) => mod.ShareDialog), {
  loading: () => null,
  ssr: false,
});
const DownloadAppMenu = dynamic(
  () => import('@/app/components/shared/Sidebar/components/DownloadAppMenu').then((mod) => mod.DownloadAppMenu),
  { loading: () => null, ssr: false },
);
const SubmitErrorDialog = dynamic(
  () => import('@/app/components/shared/SubmitErrorDialog').then((mod) => mod.SubmitErrorDialog),
  { loading: () => null, ssr: false },
);
const UpscaleDialog = dynamic(() => import('@/app/components/shared/UpscaleDialog').then((mod) => mod.UpscaleDialog), {
  loading: () => null,
  ssr: false,
});

const ClawPassUsageDialog = dynamic(
  () => import('@/app/components/shared/ClawPassUsageDialog').then((mod) => mod.ClawPassUsageDialog),
  { loading: () => null, ssr: false },
);
const ClawPassContentDialog = dynamic(
  () => import('@/app/components/shared/ClawPassContentDialog').then((mod) => mod.ClawPassContentDialog),
  { loading: () => null, ssr: false },
);
const ClawRetentionDialog = dynamic(
  () => import('@/app/components/shared/ClawRetentionDialog').then((mod) => mod.ClawRetentionDialog),
  { loading: () => null, ssr: false },
);
const ClawAgreementChangeDialog = dynamic(
  () => import('@/app/components/shared/ClawAgreementChangeDialog').then((mod) => mod.ClawAgreementChangeDialog),
  { loading: () => null, ssr: false },
);
const PaymentManager = dynamic(() => import('@/app/components/shared/Pay').then((mod) => mod.PaymentManager), {
  loading: () => null,
  ssr: false,
});

const CancelReasonQuestionnaire = dynamic(
  () =>
    import('@/app/components/shared/RetentionStrategy/components/CancelReasonQuestionnaire').then(
      (mod) => mod.CancelReasonQuestionnaire,
    ),
  { loading: () => null, ssr: false },
);
const RenameOnConflictDialog = dynamic(
  () => import('@/app/components/shared/RenameOnConflictDialog').then((mod) => mod.RenameOnConflictDialog),
  { loading: () => null, ssr: false },
);

/**
 * 弹窗错误边界组件
 * 捕获单个弹窗的错误，防止影响其他弹窗和页面
 */
interface ModalErrorBoundaryProps {
  children: ReactNode;
  modalName: string;
  onError?: (error: Error, modalName: string) => void;
  onClose: () => void;
}

/**
 * 错误边界包装器 - 使用 class 组件实现
 */
interface ErrorBoundaryWrapperProps {
  children: ReactNode;
  onError: (error: Error) => void;
}

class ErrorBoundaryWrapper extends Component<ErrorBoundaryWrapperProps, { hasError: boolean }> {
  constructor(props: ErrorBoundaryWrapperProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    this.props.onError(error);
  }

  render() {
    if (this.state.hasError) {
      return null; // 直接返回 null，不显示任何内容
    }

    return this.props.children;
  }
}

const ModalErrorBoundary: FC<ModalErrorBoundaryProps> = ({ children, modalName, onError, onClose }) => {
  const handleError = (error: Error) => {
    onError?.(error, modalName);
    // 自动关闭出错的弹窗
    setTimeout(onClose, 100);
  };

  return <ErrorBoundaryWrapper onError={handleError}>{children}</ErrorBoundaryWrapper>;
};

/**
 * 全局弹窗管理器
 * 统一管理所有弹窗组件的渲染
 */
export const GlobalModalManager: FC = () => {
  const pathname = usePathname();
  const previousPathnameRef = useRef<string | undefined>(undefined);
  const [shouldMountIdleRuntime, setShouldMountIdleRuntime] = useState(false);

  const { modals, closeModal, closeModalsOnRouteChange, paymentPopupState, payProduct } = useStore((state) => ({
    modals: state.modals,
    closeModal: state.closeModal,
    closeModalsOnRouteChange: state.closeModalsOnRouteChange,
    paymentPopupState: state.paymentPopupState,
    payProduct: state.payProduct,
  }));

  // 错误处理函数
  const handleModalError = (error: Error, modalName: string) => {
    console.error(`Modal error in ${modalName}:`, error);
  };

  useEffect(() => {
    const previousPathname = previousPathnameRef.current;

    closeModalsOnRouteChange(pathname, previousPathname);

    previousPathnameRef.current = pathname;
  }, [pathname, closeModalsOnRouteChange]);

  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(() => {
        setShouldMountIdleRuntime(true);
      });

      return () => {
        window.cancelIdleCallback(idleId);
      };
    }

    const timeoutId = globalThis.setTimeout(() => {
      setShouldMountIdleRuntime(true);
    }, 1);

    return () => {
      globalThis.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <>
      {/* 邮件订阅弹窗 */}
      {modals.newsletterSubscriptionDialog && (
        <ModalErrorBoundary
          modalName="邮件订阅"
          onError={handleModalError}
          onClose={() => {
            modals.newsletterSubscriptionDialog?.onClose?.();
            closeModal('newsletterSubscriptionDialog');
          }}
        >
          <NewsletterSubscriptionDialog
            {...modals.newsletterSubscriptionDialog}
            onClose={() => {
              modals.newsletterSubscriptionDialog?.onClose?.();
              closeModal('newsletterSubscriptionDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 积分详情弹窗 */}
      {modals.creditDetailsDialog && (
        <ModalErrorBoundary
          modalName="积分详情"
          onError={handleModalError}
          onClose={() => {
            modals.creditDetailsDialog?.onClose?.();
            closeModal('creditDetailsDialog');
          }}
        >
          <CreditDetailsDialog
            {...modals.creditDetailsDialog}
            onClose={(type?: string) => {
              modals.creditDetailsDialog?.onClose?.(type);
              closeModal('creditDetailsDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* ClawPass 用量详情弹窗 */}
      {modals.clawPassUsageDialog && (
        <ModalErrorBoundary
          modalName="ClawPass 用量详情"
          onError={handleModalError}
          onClose={() => {
            modals.clawPassUsageDialog?.onClose?.();
            closeModal('clawPassUsageDialog');
          }}
        >
          <ClawPassUsageDialog
            {...modals.clawPassUsageDialog}
            onClose={() => {
              modals.clawPassUsageDialog?.onClose?.();
              closeModal('clawPassUsageDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 任务中心抽屉（免费积分任务 + 新手创作挑战合并后的入口） */}
      {modals.taskCenterDrawer && (
        <ModalErrorBoundary
          modalName="任务中心"
          onError={handleModalError}
          onClose={() => {
            modals.taskCenterDrawer?.onClose?.();
            closeModal('taskCenterDrawer');
          }}
        >
          <TaskCenterDrawer
            {...modals.taskCenterDrawer}
            onClose={(type?: string) => {
              modals.taskCenterDrawer?.onClose?.(type);
              closeModal('taskCenterDrawer');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 问卷弹窗 */}
      {modals.questionnaire && (
        <ModalErrorBoundary
          modalName="问卷"
          onError={handleModalError}
          onClose={() => {
            modals.questionnaire?.onClose?.();
            closeModal('questionnaire');
          }}
        >
          <Questionnaire
            {...modals.questionnaire}
            onClose={() => {
              modals.questionnaire?.onClose?.();
              closeModal('questionnaire');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 套餐详情弹窗 */}
      {modals.planDetailDialog && (
        <ModalErrorBoundary
          modalName="套餐详情"
          onError={handleModalError}
          onClose={() => {
            modals.planDetailDialog?.onClose?.();
            closeModal('planDetailDialog');
          }}
        >
          <PlanDetailDialog
            {...modals.planDetailDialog}
            onClose={() => {
              modals.planDetailDialog?.onClose?.();
              closeModal('planDetailDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* ClawPass 订阅详情弹窗 */}
      {modals.clawPassContentDialog && (
        <ModalErrorBoundary
          modalName="ClawPass 订阅详情"
          onError={handleModalError}
          onClose={() => {
            modals.clawPassContentDialog?.onClose?.();
            closeModal('clawPassContentDialog');
          }}
        >
          <ClawPassContentDialog
            {...modals.clawPassContentDialog}
            onClose={() => {
              modals.clawPassContentDialog?.onClose?.();
              closeModal('clawPassContentDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* ClawPass 关闭挽留弹窗 */}
      {modals.clawRetentionDialog && (
        <ModalErrorBoundary
          modalName="ClawPass 挽留"
          onError={handleModalError}
          onClose={() => {
            modals.clawRetentionDialog?.onClose?.();
            closeModal('clawRetentionDialog');
          }}
        >
          <ClawRetentionDialog
            {...modals.clawRetentionDialog}
            onClose={() => {
              modals.clawRetentionDialog?.onClose?.();
              closeModal('clawRetentionDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* ClawPass 购买协议变更提示弹窗 */}
      {modals.clawAgreementChangeDialog && (
        <ModalErrorBoundary
          modalName="ClawPass 协议变更"
          onError={handleModalError}
          onClose={() => {
            modals.clawAgreementChangeDialog?.onClose?.();
            closeModal('clawAgreementChangeDialog');
          }}
        >
          <ClawAgreementChangeDialog
            {...modals.clawAgreementChangeDialog}
            onClose={() => {
              modals.clawAgreementChangeDialog?.onClose?.();
              closeModal('clawAgreementChangeDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 购买积分弹窗 */}
      {modals.purchaseCreditsDialog && (
        <ModalErrorBoundary
          modalName="购买积分"
          onError={handleModalError}
          onClose={() => {
            modals.purchaseCreditsDialog?.onClose?.();
            closeModal('purchaseCreditsDialog');
          }}
        >
          <PurchaseCreditsDialog
            {...modals.purchaseCreditsDialog}
            onClose={() => {
              modals.purchaseCreditsDialog?.onClose?.();
              closeModal('purchaseCreditsDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 引导下载APP弹窗 */}
      {modals.downloadAppDialog && (
        <ModalErrorBoundary
          modalName="下载APP"
          onError={handleModalError}
          onClose={() => {
            modals.downloadAppDialog?.onClose?.();
            closeModal('downloadAppDialog');
          }}
        >
          <DownloadAppMenu
            {...modals.downloadAppDialog}
            onClose={() => {
              modals.downloadAppDialog?.onClose?.();
              closeModal('downloadAppDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 操作弹窗，主要用于任务操作，包含举报、删除、分享、反馈、超分等 */}
      {modals.actionDialog && (
        <ModalErrorBoundary
          modalName="操作弹窗"
          onError={handleModalError}
          onClose={() => {
            modals.actionDialog?.onClose?.();
            closeModal('actionDialog');
          }}
        >
          <ActionDialog
            {...modals.actionDialog}
            onClose={() => {
              modals.actionDialog?.onClose?.();
              closeModal('actionDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 分享弹窗 */}
      {modals.shareDialog && (
        <ModalErrorBoundary
          modalName="分享"
          onError={handleModalError}
          onClose={() => {
            modals.shareDialog?.onClose?.();
            closeModal('shareDialog');
          }}
        >
          <ShareDialog
            {...modals.shareDialog}
            onClose={() => {
              modals.shareDialog?.onClose?.();
              closeModal('shareDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 删除弹窗 */}
      {modals.deleteDialog && (
        <ModalErrorBoundary
          modalName="删除确认"
          onError={handleModalError}
          onClose={() => {
            modals.deleteDialog?.onClose?.();
            closeModal('deleteDialog');
          }}
        >
          <DeleteDialog
            {...modals.deleteDialog}
            onClose={() => {
              modals.deleteDialog?.onClose?.();
              closeModal('deleteDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 超分弹窗 */}
      {modals.upscaleDialog && (
        <ModalErrorBoundary
          modalName="超分"
          onError={handleModalError}
          onClose={() => {
            modals.upscaleDialog?.onClose?.();
            closeModal('upscaleDialog');
          }}
        >
          <UpscaleDialog
            {...modals.upscaleDialog}
            onClose={() => {
              modals.upscaleDialog?.onClose?.();
              closeModal('upscaleDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 确认对话框 */}
      {modals.confirmDialog && (
        <ModalErrorBoundary
          modalName="确认对话框"
          onError={handleModalError}
          onClose={() => {
            modals.confirmDialog?.onClose?.();
            closeModal('confirmDialog');
          }}
        >
          {modals.confirmDialog.theme === 'admission' ? (
            <AdmissionConfirmDialog
              {...modals.confirmDialog}
              onClose={() => {
                modals.confirmDialog?.onClose?.();
                closeModal('confirmDialog');
              }}
            />
          ) : (
            <ConfirmDialog
              {...modals.confirmDialog}
              onClose={() => {
                modals.confirmDialog?.onClose?.();
                closeModal('confirmDialog');
              }}
            />
          )}
        </ModalErrorBoundary>
      )}
      {/* 投稿弹窗 */}
      {modals.postsModal && (
        <ModalErrorBoundary
          modalName="投稿弹窗"
          onError={handleModalError}
          onClose={() => {
            modals.postsModal?.onClose?.(false);
            closeModal('postsModal');
          }}
        >
          <PostsModal
            {...modals.postsModal}
            onClose={(isPosts: boolean) => {
              modals.postsModal?.onClose?.(isPosts);
              closeModal('postsModal');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 简单版生成物详情弹窗 */}
      {modals.simpleCreationDetailModal && (
        <ModalErrorBoundary
          modalName="简单版生成物详情"
          onError={handleModalError}
          onClose={() => {
            modals.simpleCreationDetailModal?.onClose?.();
            closeModal('simpleCreationDetailModal');
          }}
        >
          <SimpleCreationDetailModal
            {...modals.simpleCreationDetailModal}
            onClose={() => {
              modals.simpleCreationDetailModal?.onClose?.();
              closeModal('simpleCreationDetailModal');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 截图弹窗 */}
      {modals.frameShotDialog && (
        <ModalErrorBoundary
          modalName="抽帧弹窗"
          onError={handleModalError}
          onClose={() => {
            modals.frameShotDialog?.onClose?.();
            closeModal('frameShotDialog');
          }}
        >
          <FrameShotDialog
            {...modals.frameShotDialog}
            onClose={() => {
              modals.frameShotDialog?.onClose?.();
              closeModal('frameShotDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 优惠券弹窗 */}
      {modals.couponDialog && (
        <ModalErrorBoundary
          modalName="优惠券弹窗"
          onError={handleModalError}
          onClose={() => {
            modals.couponDialog?.onClose?.();
            closeModal('couponDialog');
          }}
        >
          <CouponDialog
            {...modals.couponDialog}
            onClose={() => {
              modals.couponDialog?.onClose?.();
              closeModal('couponDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 提交错误弹窗（由 use-dialogs 重构出的全局挂载） */}
      {modals.submitErrorDialog && (
        <ModalErrorBoundary
          modalName="提交错误弹窗"
          onError={handleModalError}
          onClose={() => {
            modals.submitErrorDialog?.onClose?.();
            closeModal('submitErrorDialog');
          }}
        >
          <SubmitErrorDialog
            {...modals.submitErrorDialog}
            onClose={() => {
              modals.submitErrorDialog?.onClose?.();
              closeModal('submitErrorDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 支付管理弹窗：支付状态由 store 保存，出现支付弹窗时再加载运行时。 */}
      {paymentPopupState && payProduct ? <PaymentManager /> : null}

      {/* 飞行动效层 */}
      {shouldMountIdleRuntime && <FlyOverlay />}

      {/* google 看广告获取积分弹窗 */}
      {modals.googleRewardedAd && (
        <ModalErrorBoundary
          modalName="google看广告获取积分弹窗"
          onError={handleModalError}
          onClose={() => {
            modals.googleRewardedAd?.onClose?.();
            closeModal('googleRewardedAd');
          }}
        >
          <GoogleRewardedAd
            {...modals.googleRewardedAd}
            onClose={() => {
              modals.googleRewardedAd?.onClose?.();
              closeModal('googleRewardedAd');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 无水印看广告获取积分弹窗 */}
      {modals.noWatermarkAdsDialog && (
        <ModalErrorBoundary
          modalName="无水印看广告获取积分弹窗"
          onError={handleModalError}
          onClose={() => {
            modals.noWatermarkAdsDialog?.onClose?.();
            closeModal('noWatermarkAdsDialog');
          }}
        >
          <NoWatermarkAdsDialog
            {...modals.noWatermarkAdsDialog}
            onClose={() => {
              modals.noWatermarkAdsDialog?.onClose?.();
              closeModal('noWatermarkAdsDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 积分不足看广告获取积分弹窗 */}
      {modals.insufficientCreditsAdsDialog && (
        <ModalErrorBoundary
          modalName="积分不足看广告获取积分弹窗"
          onError={handleModalError}
          onClose={() => {
            modals.insufficientCreditsAdsDialog?.onClose?.();
            closeModal('insufficientCreditsAdsDialog');
          }}
        >
          <InsufficientCreditsAdsDialog
            {...modals.insufficientCreditsAdsDialog}
            onClose={() => {
              modals.insufficientCreditsAdsDialog?.onClose?.();
              closeModal('insufficientCreditsAdsDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* Agent 邀请弹窗 */}
      {modals.agentInviteDialog && (
        <ModalErrorBoundary
          modalName="Agent 邀请弹窗"
          onError={handleModalError}
          onClose={() => {
            modals.agentInviteDialog?.onClose?.();
            closeModal('agentInviteDialog');
          }}
        >
          <AgentInviteDialog
            {...modals.agentInviteDialog}
            onClose={() => {
              modals.agentInviteDialog?.onClose?.();
              closeModal('agentInviteDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 取消续费原因问卷弹窗 */}
      {modals.cancelReasonQuestionnaire && (
        <ModalErrorBoundary
          modalName="取消续费原因问卷"
          onError={handleModalError}
          onClose={() => {
            modals.cancelReasonQuestionnaire?.onClose?.();
            closeModal('cancelReasonQuestionnaire');
          }}
        >
          <CancelReasonQuestionnaire
            {...modals.cancelReasonQuestionnaire}
            onClose={() => {
              modals.cancelReasonQuestionnaire?.onClose?.();
              closeModal('cancelReasonQuestionnaire');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 上传同名冲突改名弹窗 */}
      {modals.renameOnConflictDialog && (
        <ModalErrorBoundary
          modalName="renameOnConflictDialog"
          onError={handleModalError}
          onClose={() => {
            modals.renameOnConflictDialog?.onClose?.();
            closeModal('renameOnConflictDialog');
          }}
        >
          <RenameOnConflictDialog
            {...modals.renameOnConflictDialog}
            onClose={() => {
              modals.renameOnConflictDialog?.onClose?.();
              closeModal('renameOnConflictDialog');
            }}
          />
        </ModalErrorBoundary>
      )}

      {/* 登录弹窗 */}
      {modals.loginDialog && (
        <ModalErrorBoundary
          modalName="登录弹窗"
          onError={handleModalError}
          onClose={() => {
            // modals.loginDialog?.onClose?.();
            closeModal('loginDialog');
          }}
        >
          <LoginDialog
            {...modals.loginDialog}
            onClose={(userInfo) => {
              modals.loginDialog?.onClose?.(userInfo);
              closeModal('loginDialog');
            }}
          />
        </ModalErrorBoundary>
      )}
    </>
  );
};
